import type { TradingAccount } from "@/lib/accounts/types";
import { getStoredBrokersValue } from "@/lib/brokers/storage";
import { getStoredChartSettingsValue } from "@/lib/chart-settings/storage";
import { getStoredChartIndicatorsValue } from "@/lib/chart-indicators/storage";
import { getStoredDrawingsSize } from "@/lib/chart-drawings/storage";
import { getStoredEconomicCalendarValue } from "@/lib/economic-calendar/storage";
import { getStoredQuotesWatchlistValue } from "@/lib/quotes-watchlist/storage";
import { getStoredSettingsValue } from "@/lib/settings/storage";
import { formatTradingAccountKey } from "@/vybe-trader-client/chart/utils/accountHelpers";
import { MAILBOX_STORAGE_KEYS } from "@/lib/localStorageKeys/mailbox";
import { accountPrefix, scanIdbMarketData } from "./idb-ops";

export type AccountCacheTypeId = "marketData" | "mailbox";
export type AppCacheTypeId =
  | "brokers"
  | "chartSettings"
  | "chartIndicators"
  | "chartDrawings"
  | "economicCalendar"
  | "quotesWatchlist"
  | "settings";

export interface CacheLineItem {
  id: string;
  label: string;
  bytes: number;
  meta?: string;
}

export interface CacheTypeRow {
  id: AccountCacheTypeId | AppCacheTypeId | "orphanMarketData";
  label: string;
  bytes: number;
  items?: CacheLineItem[];
}

export interface AccountCacheInventory {
  accountId: string;
  companyName: string;
  serverName: string;
  label: string;
  totalBytes: number;
  types: CacheTypeRow[];
}

export interface AppCacheInventory {
  totalBytes: number;
  types: CacheTypeRow[];
}

export interface StorageInventory {
  app: AppCacheInventory;
  accounts: AccountCacheInventory[];
  orphan: CacheTypeRow | null;
  totalBytes: number;
}

function utf16Size(raw: string | null): number {
  if (!raw) return 0;
  return raw.length * 2;
}

function mailboxBytesForKey(mailboxKey: string | null): number {
  if (!mailboxKey || typeof window === "undefined") return 0;
  try {
    const raw = localStorage.getItem(MAILBOX_STORAGE_KEYS.BY_ACCOUNT);
    if (!raw) return 0;
    const all = JSON.parse(raw) as Record<string, unknown>;
    const messages = all[mailboxKey];
    if (!messages) return 0;
    return utf16Size(JSON.stringify(messages));
  } catch {
    return 0;
  }
}

function buildAppInventory(): AppCacheInventory {
  const types: CacheTypeRow[] = [
    {
      id: "brokers",
      label: "Brokers",
      bytes: getStoredBrokersValue(),
    },
    {
      id: "chartSettings",
      label: "Chart settings",
      bytes: getStoredChartSettingsValue(),
    },
    {
      id: "chartIndicators",
      label: "Chart indicators",
      bytes: getStoredChartIndicatorsValue(),
    },
    {
      id: "chartDrawings",
      label: "Chart drawings",
      bytes: getStoredDrawingsSize(),
    },
    {
      id: "economicCalendar",
      label: "Economic calendar",
      bytes: getStoredEconomicCalendarValue(),
    },
    {
      id: "quotesWatchlist",
      label: "Quotes watchlist",
      bytes: getStoredQuotesWatchlistValue(),
    },
    {
      id: "settings",
      label: "App settings",
      bytes: getStoredSettingsValue(),
    },
  ];
  const totalBytes = types.reduce((s, t) => s + t.bytes, 0);
  return { totalBytes, types };
}

function accountLabel(account: TradingAccount): string {
  return `${account.credentials.login} @ ${account.serverName}`;
}

export async function getStorageInventory(
  accounts: TradingAccount[],
): Promise<StorageInventory> {
  const app = buildAppInventory();
  const knownPrefixes = accounts.map((a) =>
    accountPrefix(a.companyName, a.serverName),
  );
  const idbScan = await scanIdbMarketData(knownPrefixes);

  const accountsInventory: AccountCacheInventory[] = accounts.map(
    (account) => {
      const prefix = accountPrefix(account.companyName, account.serverName);
      const idbRow = idbScan.byPrefix.get(prefix);
      const marketItems: CacheLineItem[] = idbRow
        ? Object.entries(idbRow.symbols)
            .map(([symbol, bytes]) => ({
              id: symbol,
              label: symbol,
              bytes,
            }))
            .sort((a, b) => b.bytes - a.bytes)
        : [];
      const marketBytes = idbRow?.totalBytes ?? 0;

      const mailboxKey = formatTradingAccountKey(account);
      const mailboxBytes = mailboxBytesForKey(mailboxKey);

      const types: CacheTypeRow[] = [
        {
          id: "marketData",
          label: "Market data",
          bytes: marketBytes,
          items: marketItems,
        },
        {
          id: "mailbox",
          label: "Mailbox",
          bytes: mailboxBytes,
        },
      ];

      const totalBytes = types.reduce((s, t) => s + t.bytes, 0);
      return {
        accountId: account.id,
        companyName: account.companyName,
        serverName: account.serverName,
        label: accountLabel(account),
        totalBytes,
        types,
      };
    },
  );

  let orphan: CacheTypeRow | null = null;
  if (idbScan.orphan.totalBytes > 0) {
    orphan = {
      id: "orphanMarketData",
      label: "Other cached data",
      bytes: idbScan.orphan.totalBytes,
      items: Object.entries(idbScan.orphan.symbols)
        .map(([id, bytes]) => ({
          id,
          label: id,
          bytes,
        }))
        .sort((a, b) => b.bytes - a.bytes),
    };
  }

  const accountsTotal = accountsInventory.reduce((s, a) => s + a.totalBytes, 0);
  const orphanTotal = orphan?.bytes ?? 0;
  const totalBytes = app.totalBytes + accountsTotal + orphanTotal;

  return {
    app,
    accounts: accountsInventory,
    orphan,
    totalBytes,
  };
}

export function findAccountInventory(
  inventory: StorageInventory,
  accountId: string,
): AccountCacheInventory | undefined {
  return inventory.accounts.find((a) => a.accountId === accountId);
}
